import { Component } from "react"
import { createPortal } from "react-dom"
import { fireSideCannons } from "src/utils/celebrate"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import StateConstants from "src/states/StateContstants"
import ApiHandler from "src/api/ApiHandler"
import navigationService from "src/navigation/NavigationService"
import LoginHelper from "./LoginHelper"
import CustomLoginPage from "./CustomLoginPage"
import { resumePendingSubscription } from "src/components/premium/Subscription"
import { resumePendingPropertyUnlock } from "src/components/premium/PropertyUnlock"
import "./LoginModal.css"
import "./LoginModalOverrides.css"
import { applyUserEntitlements } from "src/utils/LocalDevAccess"
import ValuationStateHelper from "src/utils/ValuationStateHelper"
import posthog from "src/consent/posthog"


interface LoginModalProps {
    stateHandler: EventAdaptingStateHandler
    loginHelper: LoginHelper
    apiHandler: ApiHandler
}

interface LoginModalState {
    isOpen: boolean
    hasPendingPayment: boolean
    initialMode: "login" | "signup"
}

const REDIRECT_AFTER_LOGIN_PATHS = ["/login", "/plans"]


class LoginModal extends Component<LoginModalProps, LoginModalState> {
    constructor(props: LoginModalProps) {
        super(props)

        this.state = {
            isOpen: false,
            hasPendingPayment: false,
            initialMode: "login",
        }

        props.stateHandler.addListener(StateConstants.SHOW_LOGIN_MODAL, (isOpen: boolean) => {
            const hasPendingPayment = !!(
                props.stateHandler.get(StateConstants.PENDING_PAYMENT_INTENT_ID)
                || localStorage.getItem("pendingPaymentIntentId")
            )
            const requestedMode = props.stateHandler.get(StateConstants.LOGIN_MODAL_MODE)
            const initialMode = requestedMode === "signup" ? "signup" : "login"

            this.setState({ isOpen, hasPendingPayment, initialMode })
            if (isOpen) {
                props.stateHandler.set(StateConstants.LOGIN_MODAL_MODE, "login")
            }
            document.body.style.overflow = isOpen ? "hidden" : ""
            document.documentElement.style.overflow = isOpen ? "hidden" : ""
            if (isOpen && hasPendingPayment) {
                this.fireConfetti()
            }
        })
    }

    fireConfetti() {
        if (sessionStorage.getItem("confettiShown")) return
        sessionStorage.setItem("confettiShown", "true")

        fireSideCannons()
    }

    close = () => {
        this.setState({ isOpen: false, initialMode: "login" })
        this.props.stateHandler.set(StateConstants.LOGIN_MODAL_MODE, "login")
        this.props.stateHandler.set(StateConstants.PENDING_SAVE_VALUATION_PAYLOAD, null)
        document.body.style.overflow = ""
        document.documentElement.style.overflow = ""
    }

    handleLoginSuccess = async () => {
        const pendingSavePayload = this.props.stateHandler.get(StateConstants.PENDING_SAVE_VALUATION_PAYLOAD)
        this.close()
        if (pendingSavePayload) {
            this.props.stateHandler.set(StateConstants.PENDING_SAVE_VALUATION_PAYLOAD, pendingSavePayload)
        }
        this.props.apiHandler.clearCache()

        const pendingPaymentIntentId = this.props.stateHandler.get(StateConstants.PENDING_PAYMENT_INTENT_ID)
            || localStorage.getItem("pendingPaymentIntentId")
        if (pendingPaymentIntentId) {
            try {
                await this.props.apiHandler.claimPayment(pendingPaymentIntentId)
                this.props.stateHandler.set(StateConstants.PENDING_PAYMENT_INTENT_ID, null)
                localStorage.removeItem("pendingPaymentIntentId")
            } catch (error) {
                console.error("Failed to claim payment:", error)
            }
        }

        try {
            const userInfo = await this.props.apiHandler.getUserInfo()
            const features: string[] = userInfo?.features || []
            applyUserEntitlements(
                this.props.stateHandler,
                features,
                userInfo?.at || "basic",
                userInfo?.free_detailed_valuation_credits_remaining ?? 0,
                userInfo?.free_unlocked_property_ids ?? [],
                userInfo?.permissions ?? [],
                userInfo?.role ?? null
            )
        } catch (error) {
            applyUserEntitlements(this.props.stateHandler, [], "basic", 0, [], [], null)
        }

        try {
            if (await resumePendingSubscription(this.props.apiHandler, this.props.stateHandler)) return
        } catch (error) {
            console.error("Failed to resume the subscription checkout:", error)
        }

        try {
            if (await resumePendingPropertyUnlock(this.props.apiHandler, this.props.stateHandler)) return
        } catch (error) {
            console.error("Failed to resume the unlock checkout:", error)
        }

        const savedPending = await this.completePendingSaveValuation()
        if (savedPending) {
            navigationService.navigateToSavedValuations()
            return
        }

        if (REDIRECT_AFTER_LOGIN_PATHS.includes(window.location.pathname)) {
            navigationService.navigateToHome()
        }
    }

    async completePendingSaveValuation(): Promise<boolean> {
        const pending = this.props.stateHandler.get(StateConstants.PENDING_SAVE_VALUATION_PAYLOAD)
        if (!pending?.address || !pending?.estimated_value) return false
        this.props.stateHandler.set(StateConstants.PENDING_SAVE_VALUATION_PAYLOAD, null)
        try {
            const response = await this.props.apiHandler.saveValuation({
                address: pending.address,
                removed_neighbours: pending.removed_neighbours ?? [],
                estimated_value: pending.estimated_value,
            })
            if (response?.saved_valuation) {
                const existing = this.props.stateHandler.get(StateConstants.SAVED_VALUATIONS)
                if (!existing || !existing["i"]) {
                    this.props.stateHandler.set(StateConstants.SAVED_VALUATIONS, { i: [response.saved_valuation] })
                } else {
                    ValuationStateHelper.addSavedValuation(this.props.stateHandler, response.saved_valuation)
                }
            }
            posthog?.capture("valuation_saved", { source: "post_login" })
            return true
        } catch (error) {
            console.error("Failed to save pending valuation after login:", error)
            return false
        }
    }

    handleLoginError = (error: string) => {
        console.error("Login modal error:", error)
    }

    render() {
        if (!this.state.isOpen) return null

        const isLoggedIn = this.props.stateHandler.get(StateConstants.IS_LOGGED_IN)

        const modalClass = "login-modal login-modal--next"
        const backdropClass = "login-modal-backdrop login-modal-backdrop--next"

        return createPortal(
            <>
                <div className={backdropClass} onClick={this.close} />
                <div className={modalClass}>
                    <button className="login-modal-close" onClick={this.close}>&#10005;</button>
                    <div className="login-modal-body">
                        {isLoggedIn ? (
                            <div className="login-modal-already-logged-in">
                                <h3>You're already logged in</h3>
                                <p>You're signed in and ready to go.</p>
                                <button className="plan-cta-button" onClick={this.close}>
                                    Close
                                </button>
                            </div>
                        ) : (
                            <>
                                {this.state.hasPendingPayment && (
                                    <div className="login-modal-payment-banner">
                                        <p className="login-modal-payment-title">Payment received</p>
                                        <p className="login-modal-payment-subtitle">Sign in or create an account to activate your Lifetime Access.</p>
                                    </div>
                                )}
                                <CustomLoginPage
                                    loginHelper={this.props.loginHelper}
                                    onLoginSuccess={this.handleLoginSuccess}
                                    onLoginError={this.handleLoginError}
                                    initialMode={this.state.initialMode}
                                />
                            </>
                        )}
                    </div>
                </div>
            </>,
            document.body
        )
    }
}

export default LoginModal
